import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import SweetAlert from "sweetalert2";
import { toast } from "react-toastify";
import PreLoader from "../../../../share-components/ui/preLoader";
import {
  getProductSustainabilityImagesApi,
  uploadProductSustainabilityImageApi,
  deleteProductSustainabilityImageApi,
} from "../../../../services/manage-product/productService";

// Kept in sync with config('product.sustainability_image_max_kb') in the backend (default
// 5120 KB = 5MB). The server remains the source of truth — this constant only gives
// editors immediate feedback before upload, same convention as
// manageHowToStyleVideoModal.jsx's MAX_STYLE_VIDEO_BYTES.
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

// The shared axios interceptor (src/utils/api.jsx) already shows a toast itself for 422
// and 401 responses (and rejects with the raw response body, not an Error, in those two
// cases) — only re-toast here when the rejection is a real AxiosError that wasn't already
// surfaced globally, so mutation failures aren't double-toasted. Same convention as the
// other manage*Modal.jsx components.
const notifyMutationError = (err, fallback) => {
  if (err && err.isAxiosError && err.response?.status !== 404) {
    toast.error(err.response?.data?.message || err.message || fallback);
  }
};

const validateFile = (file) => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return "Only JPG, JPEG, PNG, or WEBP images are allowed.";
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return `Image exceeds the ${Math.round(MAX_IMAGE_BYTES / (1024 * 1024))}MB size limit.`;
  }
  return null;
};

/**
 * One upload/replace/remove slot for a single sustainability category. Each card manages
 * its own file input and busy/error state independently of its siblings — there are 14 of
 * these rendered per product, each an isolated single-asset flow mirroring
 * manageHowToStyleVideoModal.jsx's video upload/replace/remove pattern.
 */
const SustainabilityCategoryCard = ({ row, productId, disabled, onUploaded, onRemoved }) => {
  const [busy, setBusy] = useState(false);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const doUpload = (file) => {
    setFileError("");
    setBusy(true);

    uploadProductSustainabilityImageApi(productId, row.category_id, file)
      .then(({ response }) => {
        toast.success(`${row.title} image uploaded.`);
        onUploaded(row.category_id, response);
      })
      .catch((err) => {
        const message = err && !err.isAxiosError && err.errors?.image?.[0]
          ? err.errors.image[0]
          : "Failed to upload the image.";
        setFileError(message);
        notifyMutationError(err, "Failed to upload the image.");
      })
      .finally(() => {
        setBusy(false);
        resetFileInput();
      });
  };

  const handleFileSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const validationMessage = validateFile(file);
    if (validationMessage) {
      setFileError(validationMessage);
      resetFileInput();
      return;
    }
    setFileError("");

    if (row.image) {
      SweetAlert.fire({
        title: `Replace ${row.title} image?`,
        html: "This will permanently remove the current image and upload the new one.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, replace",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      }).then((result) => {
        if (!result.isConfirmed) {
          resetFileInput();
          return;
        }
        doUpload(file);
      });
    } else {
      doUpload(file);
    }
  };

  const handleDelete = () => {
    SweetAlert.fire({
      title: `Delete ${row.title} image?`,
      html: "This will permanently remove the current image.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) return;

      setBusy(true);
      deleteProductSustainabilityImageApi(row.id)
        .then(() => {
          toast.success(`${row.title} image deleted.`);
          onRemoved(row.category_id);
        })
        .catch((err) => notifyMutationError(err, "Failed to delete the image."))
        .finally(() => setBusy(false));
    });
  };

  const cardBusy = busy || disabled;

  return (
    <div className="border rounded p-2 mb-3" style={{ position: "relative" }}>
      {busy && <PreLoader withoutText />}
      <div className="font-weight-bold mb-2">{row.title}</div>

      {row.image ? (
        <img
          src={row.image}
          alt={row.title}
          style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 4, marginBottom: 8 }}
        />
      ) : (
        <div
          className="d-flex align-items-center justify-content-center text-muted mb-2"
          style={{ height: 120, border: "1px dashed #ccc", borderRadius: 4, fontSize: "0.85rem" }}
        >
          No image uploaded yet.
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        className="form-control-file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        disabled={cardBusy}
        style={{ display: row.image ? "none" : "block" }}
        onChange={handleFileSelected}
      />

      {fileError && <div className="text-danger small mt-1">{fileError}</div>}

      {row.image && (
        <div className="d-flex" style={{ gap: 8 }}>
          <Button
            size="sm"
            color="primary"
            disabled={cardBusy}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            Replace
          </Button>
          <Button size="sm" color="danger" outline disabled={cardBusy} onClick={handleDelete}>
            Delete
          </Button>
        </div>
      )}
    </div>
  );
};

const ManageSustainabilityImagesModal = ({ open, onClose, productId, productCode, productColour }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const fetchEntries = (silent = false) => {
    if (!productId) return;
    if (!silent) {
      setLoading(true);
      setLoadError("");
    }
    getProductSustainabilityImagesApi(productId)
      .then(({ response }) => {
        setEntries(Array.isArray(response) ? response : []);
      })
      .catch((err) => {
        if (!silent) {
          setLoadError(err?.response?.data?.message || err?.message || "Failed to load Sustainability images.");
        }
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  useEffect(() => {
    // Fully clear stale entries/product scope so a later reopen never flashes a previous
    // product's rows, whether we're closing or switching to a different productId.
    setEntries([]);
    setLoadError("");
    setLoading(false);

    if (open && productId) {
      fetchEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId]);

  const handleUploaded = (categoryId, record) => {
    setEntries((prev) =>
      prev.map((row) => (row.category_id === categoryId ? { ...row, id: record.id, image: record.image } : row))
    );
    fetchEntries(true);
  };

  const handleRemoved = (categoryId) => {
    setEntries((prev) => prev.map((row) => (row.category_id === categoryId ? { ...row, id: null, image: null } : row)));
    fetchEntries(true);
  };

  return (
    <Modal isOpen={open} toggle={onClose} className="modal-dialog-centered" size="lg">
      <ModalHeader toggle={onClose} tag="div">
        <div>Sustainability</div>
        {(productCode || (Array.isArray(productColour) && productColour.filter(Boolean).length > 0)) && (
          <div style={{ fontSize: "0.8rem", fontWeight: 400, color: "#888", marginTop: 2 }}>
            {[productCode, Array.isArray(productColour) ? productColour.filter(Boolean).join(", ") : productColour]
              .filter(Boolean)
              .join(" · ")}
          </div>
        )}
      </ModalHeader>
      <ModalBody style={{ minHeight: 200, position: "relative", maxHeight: "70vh", overflowY: "auto" }}>
        {loading && <PreLoader withoutText />}

        {!loading && loadError && (
          <div className="alert alert-danger">
            <div className="font-weight-bold mb-1">Sustainability images could not be loaded</div>
            <div className="mb-2">{loadError}</div>
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => fetchEntries()}>
              Retry
            </button>
          </div>
        )}

        {!loading && !loadError && (
          <div className="row">
            {entries.map((row) => (
              <div className="col-6 col-md-4" key={row.category_id}>
                <SustainabilityCategoryCard
                  row={row}
                  productId={productId}
                  disabled={false}
                  onUploaded={handleUploaded}
                  onRemoved={handleRemoved}
                />
              </div>
            ))}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ManageSustainabilityImagesModal;
